import { useTranslation } from 'react-i18next';
import Form from './form';
import Header from '../offer-package/header';
import { INotificationService, NotificationService } from '../services';
import Overlay from '../ui/overlay';
import {
  axiosMod,
  getGaClientId,
  getGaSessionId,
  getGaUserIdFromEmail,
  getPublicApiUrl,
  gtmEvent,
} from '../utility';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { IEnquiryOptionDto } from '../model';
import { EnquiryState, IEnquiryState } from './state';
import { Helmet } from 'react-helmet-async';
import Footer from './footer';
import LanguageSwitch from './language-switch';

const notificationService: INotificationService = new NotificationService();
const enquiryState: IEnquiryState = new EnquiryState();

function buildOptions(
  names: string[],
  selectedName?: string | null,
  fallbackSelectedIndex?: number
): IEnquiryOptionDto[] {
  return names.map((name, index) => ({
    name,
    valueEnabled: selectedName
      ? name.toLowerCase().includes(selectedName.toLowerCase()) ||
        selectedName.toLowerCase().includes(name.toLowerCase())
      : index === fallbackSelectedIndex,
  }));
}

export function EnquirySitaramPage() {
  const { t, i18n } = useTranslation('anfrage-sitaram');
  const q = useSearchParams();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const foundTreatment = searchParams.get('treatment');
  const foundRoom = searchParams.get('room');
  const foundCode = searchParams.get('code');

  const treatmentOptions: IEnquiryOptionDto[] = [
    ...buildOptions(
      [
        t('packages.pancha-7.name'),
        t('packages.pancha-classic.name'),
        t('packages.pancha-14.name'),
        t('packages.transformation.name'),
        t('packages.retreat-only.name'),
        t('addons.consultation.title'),
      ],
      foundTreatment,
      4
    ),
    {
      name: t('addons.masterclass.title'),
      valueEnabled: true,
    },
  ];

  const roomOptions: IEnquiryOptionDto[] = buildOptions(
    [
      t('rooms.vintage-classic'),
      t('rooms.vintage-balcony'),
      t('rooms.vintage-ac'),
      t('rooms.vintage-balcony-ac'),
      t('rooms.ayurveda-classic-balcony'),
      t('rooms.ayurveda-french-balcony'),
      t('rooms.ayurveda-garden'),
      t('rooms.please-advise'),
      t('occupancy.single'),
      t('occupancy.double'),
    ],
    foundRoom,
    7
  );

  if (!roomOptions.slice(8).some((option) => option.valueEnabled)) {
    roomOptions[8].valueEnabled = true;
  }

  const pricingOptions: IEnquiryOptionDto[] = [];

  useEffect(() => {
    gtmEvent('enquiry_start');
  }, []);

  return (
    <div className="min-h-screen bg-brand-cashmere-50 text-slate-900">
      <Overlay service={notificationService}></Overlay>

      <Helmet htmlAttributes={{ lang: i18n.language }} />

      <Header actions={<LanguageSwitch></LanguageSwitch>}></Header>

      <section className="relative overflow-hidden px-6 py-16 text-center sm:px-8 lg:py-20">
        <p className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-brand-green-700">
          {t('hero.eyebrow')}
        </p>

        <h1 className="mx-auto max-w-4xl font-serif text-5xl leading-none text-brand-cashmere-900 sm:text-6xl lg:text-7xl">
          {t('hero.title-prefix')}{' '}
          <span className="italic text-brand-green-700">{t('hero.title-em')}</span>
        </h1>

        <p className="mx-auto mt-7 max-w-2xl text-lg font-light leading-8 text-slate-700">
          {t('hero.lead')}
        </p>

        <div className="mx-auto mt-10 flex max-w-3xl flex-wrap justify-center gap-x-10 gap-y-5 border-y border-slate-200 px-6 py-5">
          <HeroMeta label={t('hero.dates-label')} value={t('hero.dates')} />
          <HeroMeta label={t('hero.venue-label')} value={t('hero.venue')} />
          <HeroMeta label={t('hero.spaces-label')} value={t('hero.spaces')} />
        </div>
      </section>

      <main className="mx-auto grid max-w-7xl gap-12 px-4 pb-24 sm:px-8 lg:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.75fr)] lg:gap-14">
        <Form
          enquiryState={enquiryState}
          roomOptions={roomOptions}
          roomCategoryCount={8}
          packageCount={5}
          code={foundCode}
          treatmentOptions={treatmentOptions}
          pricingOptions={pricingOptions}
          onSubmit={async (data) => {
            const userId = await getGaUserIdFromEmail(data.contactMail);
            const clientId = getGaClientId();
            const sessionId = getGaSessionId();

            if (q[0].get('test') === 'yes') {
              console.log('found test');
              console.log('client_id', clientId);
              console.log('user_id', userId);
              console.log('session_id', sessionId);
              console.log('data', data);

              gtmEvent('enquiry_finished', userId);

              return;
            }

            await axiosMod()
              .post(getPublicApiUrl('/enquiry'), {
                ...data,
                userId: userId,
                clientId: clientId,
                sessionId: sessionId,
                contactLanguage: i18n.language.toLowerCase(),
              })
              .then(() => {
                gtmEvent('enquiry_finished', userId);

                notificationService.createNotification({
                  type: 'success',
                  title: t('notifications.success-title'),
                  description: t('notifications.success-description'),
                });
              })
              .finally(() => {
                navigate('finish');
              });
          }}
        ></Form>

        <aside className="space-y-5 lg:sticky lg:bottom-6 lg:self-end">
          <section className="overflow-hidden rounded-2xl bg-brand-green-700 p-8 text-white shadow-xl">
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.3em] text-white">
              {t('sidebar.eyebrow')}
            </p>
            <h2 className="font-serif text-3xl leading-tight text-white">
              {t('sidebar.title')}
            </h2>
            <p className="mt-5 text-sm font-light leading-7">
              {t('sidebar.description')}
            </p>

            <ul className="mt-6 divide-y divide-white/20">
              {[0, 1, 2, 3, 4].map((index) => (
                <li key={index} className="py-3 text-sm">
                  <span className="mr-3 text-white">*</span>
                  {t(`sidebar.bullets.${index}`)}
                </li>
              ))}
            </ul>

            <blockquote className="mt-6 border-l-2 border-white pl-5 font-serif text-lg italic leading-7 text-white">
              {t('sidebar.quote')}
              <footer className="mt-3 font-sans text-xs not-italic uppercase tracking-[0.18em] text-white/80">
                {t('sidebar.quote-source')}
              </footer>
            </blockquote>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-7">
            <h2 className="font-serif text-2xl text-brand-cashmere-900">
              {t('contact-card.title')}
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-700">
              <strong className="font-medium text-brand-cashmere-900">
                Ayurveda Resort Mandira
              </strong>
              <br />
              Wagerberg 120 · 8271 Bad Waltersdorf
              <br />
              {t('contact-card.country')}
              <br />
              <br />
              {t('contact-card.phone')}:{' '}
              <a className="border-b border-brand-green-700" href="tel:+4333332801">
                +43 3333 2801
              </a>
              <br />
              E-Mail:{' '}
              <a
                className="border-b border-brand-green-700"
                href="mailto:info@mandira-ayurveda.at"
              >
                info@mandira-ayurveda.at
              </a>
            </p>
          </section>
        </aside>
      </main>

      <Footer></Footer>
    </div>
  );
}

function HeroMeta(props: { label: string; value: string }) {
  return (
    <div className="text-center">
      <span className="text-xs uppercase tracking-[0.2em] text-brand-cashmere-900">
        {props.label}
      </span>
      <strong className="mt-1 block font-serif text-xl font-medium tracking-wide text-brand-cashmere-900">
        {props.value}
      </strong>
    </div>
  );
}

export default EnquirySitaramPage;
